/**
 * uploadCancelToast.js — Single source of truth for the
 * "Upload was cancelled" toast.
 *
 * Why this exists
 * ---------------
 * Cancelling a bank-statement upload used to fire the same toast 3+
 * times because at least six different call-sites all called
 * `toast.info("<file> upload cancelled")` independently:
 *   1. BackgroundJobsContext poll → status===cancelled
 *   2. BankFeedCenterPage local poll → status===cancelled
 *   3. BankFeedCenterPage handleBacklogCancel (explicit dialog cancel)
 *   4. BankFeedCenterPage handleCancelJob (banner X button)
 *   5. BankFeedCenterPage in-page poller cancelled branch (line 2508)
 *   6. GlobalUploadBacklogModalProvider handleCancel (modal cancel)
 * When two or more of these observed the same cancel within the same
 * session, the user saw stacked duplicate toasts.
 *
 * Contract
 * --------
 * Call `notifyUploadCancelled(jobId, filename, opts?)` from any of the
 * sites above. The helper:
 *   - fires exactly ONE toast per jobId within a short dedupe window
 *     (default 30 s, well beyond any polling burst)
 *   - falls back to a filename-based key if no jobId is passed
 *   - supports two variants via `opts.reason`:
 *       'payment_failed'          → red toast, "<file> cancelled: <msg>"
 *       'user_cancelled_import'   → info toast, "<file> upload cancelled. Nothing was imported."
 *       (default)                 → info toast, "<file> upload cancelled."
 *
 * Keeping the dedupe map in module scope (not React state) is
 * intentional — every call site reaches through the same JS module
 * instance so this works across pages, providers, and pollers.
 */
import { toast } from 'sonner';

const DEDUPE_WINDOW_MS = 30_000;
const _recent = new Map(); // key -> timestamp

function _keyFor(jobId, filename) {
  return jobId ? `job:${jobId}` : `file:${filename || 'unknown'}`;
}

function _isDuplicate(key) {
  const now = Date.now();
  // Opportunistic cleanup of stale entries so the map cannot grow unbounded.
  for (const [k, ts] of _recent) {
    if (now - ts > DEDUPE_WINDOW_MS) _recent.delete(k);
  }
  const last = _recent.get(key);
  if (last && now - last < DEDUPE_WINDOW_MS) return true;
  _recent.set(key, now);
  return false;
}

/**
 * Fire the "upload cancelled" toast at most once per jobId per session.
 * Safe to call from every observer of the cancel event; only the first
 * caller within the dedupe window will render the toast.
 */
export function notifyUploadCancelled(jobId, filename, opts = {}) {
  const key = _keyFor(jobId, filename);
  if (_isDuplicate(key)) return false;

  const safeName = filename || 'Upload';
  const reason = opts.reason || 'user_cancelled';

  if (reason === 'payment_failed') {
    toast.error(`"${safeName}" cancelled: ${opts.message || 'payment declined'}`);
    return true;
  }
  if (reason === 'user_cancelled_import') {
    toast.info(`"${safeName}" upload cancelled. Nothing was imported.`);
    return true;
  }
  // default
  toast.info(`"${safeName}" upload cancelled.`);
  return true;
}

/**
 * Testing / edge-case escape hatch — clears the dedupe map so a
 * subsequent identical toast fires immediately. Not used by product
 * code today; kept for unit tests and DevTools.
 */
export function _resetUploadCancelDedupe() {
  _recent.clear();
}
